"use client";

import { useState, useRef, useEffect } from "react";

interface HtmlContentWithReadMoreProps {
  html: string;
  maxWords?: number;
  className?: string;
}

export function HtmlContentWithReadMore({
  html,
  maxWords = 30,
  className = "",
}: HtmlContentWithReadMoreProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const defaultClasses = "prose prose-sm max-w-none focus:outline-none [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>ul]:my-1 [&>ol]:my-1 [&>ul>li]:my-0 [&>ol>li]:my-0 prose-li:my-0 prose-li:leading-normal [&_li]:my-0 [&_li_p]:m-0";

  // Function to get word count from HTML string
  const getWordCount = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText;
    return text.trim().split(/\s+/).length;
  };

  useEffect(() => {
    const wordCount = getWordCount(html);
    setShouldShowButton(wordCount > maxWords);
  }, [html, maxWords]);

  // Function to truncate HTML content
  const getTruncatedHtml = () => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText;
    const words = text.trim().split(/\s+/);
    
    if (words.length <= maxWords) return html;
    
    let currentWord = 0;
    let result = '';
    const walk = (node: Node) => {
      if (currentWord >= maxWords) return;
      
      if (node.nodeType === 3) { // Text node
        const words = node.textContent?.trim().split(/\s+/) || [];
        const remainingWords = maxWords - currentWord;
        if (words.length <= remainingWords) {
          result += node.textContent;
          currentWord += words.length;
        } else {
          result += words.slice(0, remainingWords).join(' ') + '...';
          currentWord = maxWords;
        }
      } else {
        const element = node as Element;
        result += `<${element.tagName.toLowerCase()}>`;
        for (const child of Array.from(node.childNodes)) {
          walk(child);
        }
        result += `</${element.tagName.toLowerCase()}>`;
      }
    };
    
    for (const child of Array.from(div.childNodes)) {
      walk(child);
    }
    
    return result;
  };

  return (
    <div>
      <div
        className={`${defaultClasses} ${className}`}
        dangerouslySetInnerHTML={{
          __html: isExpanded ? html : getTruncatedHtml(),
        }}
      />
      {shouldShowButton && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
        >
          {isExpanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
} 